// Module-level singleton bridging the Timeline's preview controls
// (`useTimelinePreview`) to call sites outside the Timeline component
// tree — currently the VideoPlayer click overlay.
//
// Timeline.tsx registers its `play`, `stop`, `playFromStart`, and an
// `isPlaying` getter on mount (via `setTimelinePlaybackController`) and
// clears them on unmount. VideoPlayer reads them through
// `getTimelinePlaybackController()` so a click on the <video> element
// triggers the same unified preview pipeline as the Timeline's own play
// button (auto-ducking, SFX bed, playhead sync).
//
// `playFromStart` exists because resuming from a stale playhead position
// after pause leaves an audio snippet from wherever the user last stopped —
// the video click should always start a fresh preview from 0.
//
// Same pattern as src/lib/activeVideo.ts — a singleton avoids threading
// callbacks through the component tree when there's exactly one Timeline
// mounted per view.

export interface TimelinePlaybackController {
  play: (startFrom?: number) => void;
  /** Seek to 0 then start playback — used by the video-click overlay so a
   *  click never resumes from a stale playhead position. */
  playFromStart: () => void;
  stop: () => void;
  /** Function so the caller always reads the latest value, not a snapshot. */
  isPlaying: () => boolean;
}

let controller: TimelinePlaybackController | null = null;

export function setTimelinePlaybackController(
  c: TimelinePlaybackController | null,
): void {
  controller = c;
}

export function getTimelinePlaybackController(): TimelinePlaybackController | null {
  return controller;
}
